// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1';

// Storage keys
export const STORAGE_KEYS = {
    ACCESS_TOKEN: 'msk_admin_access_token',
    REFRESH_TOKEN: 'msk_admin_refresh_token',
    ADMIN_DATA: 'msk_admin_data'
};

// Create headers with authorization
const getHeaders = (includeAuth = true) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    if (includeAuth) {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    return headers;
};

// Handle API response
const handleResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
        // If token expired, try to refresh — only when backend explicitly says TOKEN_EXPIRED
        if (response.status === 401 && data.errorCode === 'TOKEN_EXPIRED') {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
                return null; // Signal to retry
            }
        }
        // عرض رسالة الخطأ الحقيقية من الباكند
        let errorMessage = data.message || 'حدث خطأ في الاتصال';
        // إذا كانت الرسالة عامة وفيه تفاصيل إضافية، أضفها
        if (data.details && typeof data.details === 'string' && data.details !== errorMessage) {
            errorMessage = `${errorMessage}: ${data.details}`;
        } else if (data.details?.message) {
            errorMessage = data.details.message;
        } else if (data.details?.missingFields?.length) {
            errorMessage = `الحقول التالية مطلوبة: ${data.details.missingFields.join(', ')}`;
        }
        const err = new Error(errorMessage);
        err.details = data.details || null;
        err.errorCode = data.errorCode || null;
        err.statusCode = response.status;
        throw err;
    }
    
    return data;
};

// Refresh access token
const refreshAccessToken = async () => {
    try {
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        if (!refreshToken) return false;

        const response = await fetch(`${API_BASE_URL}/admin/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken })
        });

        if (!response.ok) {
            // Clear tokens and redirect to login
            clearTokens();
            window.location.href = '/login';
            return false;
        }

        const data = await response.json();
        if (data.success && data.data) {
            const storage = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ? localStorage : sessionStorage;
            storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.data.accessToken);
            storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.data.refreshToken);
            return true;
        }

        return false;
    } catch (error) {
        console.error('Token refresh failed:', error);
        clearTokens();
        return false;
    }
};

// Clear all tokens
export const clearTokens = () => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ADMIN_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.ADMIN_DATA);
};

// API request wrapper with retry logic
const apiRequest = async (url, options = {}, retry = true) => {
    try {
        const response = await fetch(`${API_BASE_URL}${url}`, {
            ...options,
            headers: getHeaders(options.includeAuth !== false)
        });

        const data = await handleResponse(response);
        
        // If null, token was refreshed, retry the request
        if (data === null && retry) {
            return apiRequest(url, options, false);
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Multipart form data request (for file uploads)
const multipartRequest = async (url, formData, method = 'POST', retry = true) => {
    try {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        const headers = {};
        
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        // Note: Don't set Content-Type for multipart, browser will set it with boundary

        const response = await fetch(`${API_BASE_URL}${url}`, {
            method,
            headers,
            body: formData
        });

        const data = await handleResponse(response);
        
        // If null, token was refreshed, retry the request
        if (data === null && retry) {
            return multipartRequest(url, formData, method, false);
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// API Methods
const api = {
    get: (url) => apiRequest(url, { method: 'GET' }),
    post: (url, body, includeAuth = true) => apiRequest(url, { 
        method: 'POST', 
        body: JSON.stringify(body),
        includeAuth 
    }),
    put: (url, body) => apiRequest(url, { method: 'PUT', body: JSON.stringify(body) }),
    patch: (url, body) => apiRequest(url, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (url) => apiRequest(url, { method: 'DELETE' }),
    // For file uploads
    postMultipart: (url, formData) => multipartRequest(url, formData, 'POST'),
    putMultipart: (url, formData) => multipartRequest(url, formData, 'PUT'),
    patchMultipart: (url, formData) => multipartRequest(url, formData, 'PATCH')
};

export default api;
